/* global Word */
import React, { useCallback, useState } from "react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode } from "@fluentui/react/lib/DetailsList";
import { Separator } from "@fluentui/react/lib/Separator";

import { TroubleshootingToolProps } from "../../troubleshooting-tools.types";
import { formatMessageDefault } from "../../../../../utils/helpers/LanguageHelper";
import { DocPropKeys, retrieveDocumentProp } from "../../../../../utils/helpers/CustomXmlHelper";
import { getCoObjectFromTagOrCustomXmlByContentControl } from "../../../../../utils/helpers/ContentControlHelper";
import { selectContentControl } from "../../../../../office/ContentControl";
import { fetchDocumentPartDetails, fetchDocumentPartNode } from "../../../../../api/document/DocumentsPart";
import { DocumentPart } from "../../../../../api/models";
import { Dropdown, IDropdownOption } from "@fluentui/react/lib/Dropdown";

interface DocPartCCLink {
  contentControlId: number;
  contentControlTitle: string;
  includedDocumentPartId: string;
  documentPartExists: boolean;
  documentPartName?: string;
  linkError?: string;
}

interface UndefinedCoObjectCC {
  contentControlId: number;
  contentControlTitle: string;
  contentControlTag: string;
}

interface AnalysisResult {
  links: DocPartCCLink[];
  totalContentControls: number;
  linkedCount: number;
  brokenLinksCount: number;
  undefinedCoObjects: UndefinedCoObjectCC[];
  bodyLevelContentControls: number;
  availableDocumentParts: DocumentPart[];
}

const fetchDocPartLinks = async (): Promise<AnalysisResult> => {
  return await Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load("items");
    await context.sync();

    const links: DocPartCCLink[] = [];
    const undefinedCoObjects: UndefinedCoObjectCC[] = [];
    let bodyLevelCount = 0;

    for (const cc of contentControls.items) {
      cc.load("id,title,tag,parentContentControlOrNullObject");
      await context.sync();

      // Check if this is a body-level content control (no parent)
      const isBodyLevel = cc.parentContentControlOrNullObject.isNullObject;
      if (isBodyLevel) {
        bodyLevelCount++;

        // Check if getCoObjectFromTagOrCustomXmlByContentControl returns an empty object
        const coObject = await getCoObjectFromTagOrCustomXmlByContentControl(cc);

        if (!coObject || Number(coObject.contentControlId) !== cc.id) {
          undefinedCoObjects.push({
            contentControlId: cc.id,
            contentControlTitle: cc.title || "(no title)",
            contentControlTag: cc.tag || "(no tag)",
          });
        } else if (coObject.info?.objectType === "DOCUMENT_PART_INCLUDE") {
          // Only include body-level content controls with DOCUMENT_PART_INCLUDE type
          const includedDocPartId = (coObject.source as any)?.includedDocumentPartId;

          if (includedDocPartId) {
            // Verify link by fetching document part details from backend
            try {
              const documentPart = await fetchDocumentPartDetails(includedDocPartId);
              links.push({
                contentControlId: cc.id,
                contentControlTitle: cc.title || "(no title)",
                includedDocumentPartId: includedDocPartId,
                documentPartExists: true,
                documentPartName: documentPart?.name || "(unnamed)",
              });
            } catch (error) {
              // Document part not found or error fetching
              links.push({
                contentControlId: cc.id,
                contentControlTitle: cc.title || "(no title)",
                includedDocumentPartId: includedDocPartId,
                documentPartExists: false,
                linkError: error instanceof Error ? error.message : "Unknown error",
              });
            }
          } else {
            // Missing includedDocumentPartId
            links.push({
              contentControlId: cc.id,
              contentControlTitle: cc.title || "(no title)",
              includedDocumentPartId: "(missing)",
              documentPartExists: false,
              linkError: "Missing includedDocumentPartId in source",
            });
          }
        }
      }
    }

    const linkedCount = links.filter((l) => l.documentPartExists).length;
    const brokenLinksCount = links.filter((l) => !l.documentPartExists).length;

    // Fetch available document parts
    let availableDocumentParts: DocumentPart[] = [];
    const documentId = await retrieveDocumentProp(DocPropKeys.DocumentId);

    if (documentId) {
      try {
        const allDocParts = await fetchDocumentPartNode(documentId);
        if (allDocParts) {
          // Filter out already linked document parts
          const linkedIds = links.filter((l) => l.documentPartExists).map((l) => l.includedDocumentPartId);

          availableDocumentParts = allDocParts.filter((docPart) => !linkedIds.includes(docPart.id || ""));
        }
      } catch (error) {
        console.error("Error fetching available document parts:", error);
      }
    }

    return {
      links,
      totalContentControls: contentControls.items.length,
      linkedCount,
      brokenLinksCount,
      undefinedCoObjects,
      bodyLevelContentControls: bodyLevelCount,
      availableDocumentParts,
    };
  });
};

export const DocPartCCLinker: React.FC<TroubleshootingToolProps> = ({ onError, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocParts, setSelectedDocParts] = useState<{ [ccId: number]: string }>({});

  const analyzeLinks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      // Verify custom properties
      const documentId = await retrieveDocumentProp(DocPropKeys.DocumentId);
      const tenantId = await retrieveDocumentProp(DocPropKeys.TenantId);

      if (!documentId || !tenantId) {
        const errorMessage = formatMessageDefault(
          "cof.troubleshooting.docpartlinker.error.notIntelligentDisclosure",
          "The analyzed document does not appear to be an Intelligent Disclosure document (missing DocumentID or TenantID custom properties)."
        );
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      const analysisResult = await fetchDocPartLinks();
      setResult(analysisResult);

      onSuccess?.(
        formatMessageDefault(
          "cof.troubleshooting.docpartlinker.onSuccess",
          `Analysis completed: ${analysisResult.links.length} DOCUMENT_PART_INCLUDE objects found at body level`
        )
      );

      console.log("Document Part Link Analysis:", analysisResult);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error occurred";
      setError(message);
      onError?.(err instanceof Error ? err : new Error(message));
    } finally {
      setIsLoading(false);
    }
  }, [onError, onSuccess]);

  const handleNavigateToContentControl = useCallback(async (contentControlId: number) => {
    try {
      await selectContentControl({ contentControlId } as any);
    } catch (error) {
      console.error("Failed to navigate to content control:", error);
    }
  }, []);

  const handleDocPartSelection = useCallback((ccId: number, selectedKey: string | number | undefined) => {
    if (selectedKey) {
      setSelectedDocParts((prev) => ({
        ...prev,
        [ccId]: selectedKey as string,
      }));
    }
  }, []);

  const handleLinkDocumentPart = useCallback(
    async (ccId: number) => {
      const selectedDocPartId = selectedDocParts[ccId];
      if (!selectedDocPartId) {
        return;
      }

      setIsLoading(true);
      try {
        // Get document ID from custom properties
        const documentId = await retrieveDocumentProp(DocPropKeys.DocumentId);
        if (!documentId) {
          throw new Error("Document ID not found");
        }

        // Find the selected document part name
        const selectedDocPart = result?.availableDocumentParts.find((dp) => dp.id === selectedDocPartId);
        const objectName = selectedDocPart?.name || "Document Part";

        // Call backend to create DOCUMENT_PART_INCLUDE object
        const { troubleshootingFixLinkDocumentPart } = await import("../../../../../api/object/CoObjectGeneral");
        const coObjectResponse = await troubleshootingFixLinkDocumentPart(
          documentId,
          ccId,
          selectedDocPartId,
          objectName
        );

        // Update content control tag with the returned CoObject
        await Word.run(async (context) => {
          const contentControl = context.document.contentControls.getById(ccId);

          // Build the complete CoObject structure for the tag (matching real tag structure)
          const coObjectTag = {
            documentId: documentId,
            contentControlId: ccId,
            id: coObjectResponse.id,
            info: {
              objectName: coObjectResponse.name || objectName,
              objectType: "DOCUMENT_PART_INCLUDE",
              overrideData: false,
              overrideStyle: false,
              isComponent: false,
            },
            source: {
              includedDocumentPartId: selectedDocPartId,
            },
            isLocked: false,
            originalPayload: null,
          };

          // Set title and tag on the content control
          contentControl.title = coObjectTag.info.objectName;
          contentControl.tag = JSON.stringify(coObjectTag);

          await context.sync();
        });

        // Refresh the analysis to show the updated state
        await analyzeLinks();

        onSuccess?.(
          formatMessageDefault(
            "cof.troubleshooting.docpartlinker.link.success",
            `Successfully linked content control ${ccId} to document part ${selectedDocPartId}`
          )
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error occurred";
        setError(message);
        onError?.(err instanceof Error ? err : new Error(message));
      } finally {
        setIsLoading(false);
      }
    },
    [selectedDocParts, result?.availableDocumentParts, onError, onSuccess, analyzeLinks]
  );

  const getDropdownOptions = useCallback((): IDropdownOption[] => {
    if (!result?.availableDocumentParts) return [];

    return result.availableDocumentParts.map((docPart) => ({
      key: docPart.id || "",
      text: docPart.name || "(unnamed)",
    }));
  }, [result?.availableDocumentParts]);

  const brokenLinksColumns: IColumn[] = [
    {
      key: "contentControlId",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.ccid", "CC ID"),
      fieldName: "contentControlId",
      minWidth: 60,
      maxWidth: 80,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span
          style={{ fontFamily: "monospace", color: "#0078d4", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => handleNavigateToContentControl(item.contentControlId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") void handleNavigateToContentControl(item.contentControlId);
          }}
          role="button"
          tabIndex={0}
        >
          {item.contentControlId}
        </span>
      ),
    },
    {
      key: "contentControlTitle",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.cctitle", "CC Title"),
      fieldName: "contentControlTitle",
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span
          style={{ cursor: "pointer", color: "#0078d4" }}
          onClick={() => handleNavigateToContentControl(item.contentControlId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") void handleNavigateToContentControl(item.contentControlId);
          }}
          role="button"
          tabIndex={0}
        >
          {item.contentControlTitle}
        </span>
      ),
    },
    {
      key: "includedDocumentPartId",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.docpartid", "Document Part ID"),
      fieldName: "includedDocumentPartId",
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{item.includedDocumentPartId}</span>
      ),
    },
    {
      key: "documentPartName",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.docpartname", "Document Part Name"),
      fieldName: "documentPartName",
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span style={{ color: item.documentPartExists ? "inherit" : "red" }}>
          {item.documentPartName || item.linkError || "-"}
        </span>
      ),
    },
    {
      key: "linkStatus",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.linkstatus", "Status"),
      fieldName: "linkStatus",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: DocPartCCLink) => (
        <span style={{ fontSize: 18, color: item.documentPartExists ? "green" : "red" }}>
          {item.documentPartExists ? "✓" : "✗"}
        </span>
      ),
    },
  ];

  const validLinksColumns: IColumn[] = [
    {
      key: "contentControlId",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.ccid", "CC ID"),
      fieldName: "contentControlId",
      minWidth: 60,
      maxWidth: 80,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span
          style={{ fontFamily: "monospace", color: "#0078d4", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => handleNavigateToContentControl(item.contentControlId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") void handleNavigateToContentControl(item.contentControlId);
          }}
          role="button"
          tabIndex={0}
        >
          {item.contentControlId}
        </span>
      ),
    },
    {
      key: "contentControlTitle",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.cctitle", "CC Title"),
      fieldName: "contentControlTitle",
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span
          style={{ cursor: "pointer", color: "#0078d4" }}
          onClick={() => handleNavigateToContentControl(item.contentControlId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") void handleNavigateToContentControl(item.contentControlId);
          }}
          role="button"
          tabIndex={0}
        >
          {item.contentControlTitle}
        </span>
      ),
    },
    {
      key: "includedDocumentPartId",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.docpartid", "Document Part ID"),
      fieldName: "includedDocumentPartId",
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{item.includedDocumentPartId}</span>
      ),
    },
    {
      key: "documentPartName",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.docpartname", "Document Part Name"),
      fieldName: "documentPartName",
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item: DocPartCCLink) => (
        <span style={{ color: item.documentPartExists ? "inherit" : "red" }}>
          {item.documentPartName || item.linkError || "-"}
        </span>
      ),
    },
    {
      key: "linkStatus",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.linkstatus", "Status"),
      fieldName: "linkStatus",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: DocPartCCLink) => (
        <span style={{ fontSize: 18, color: item.documentPartExists ? "green" : "red" }}>
          {item.documentPartExists ? "✓" : "✗"}
        </span>
      ),
    },
  ];

  const undefinedCoObjectColumns: IColumn[] = [
    {
      key: "contentControlId",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.ccid", "CC ID"),
      fieldName: "contentControlId",
      minWidth: 60,
      maxWidth: 80,
      isResizable: true,
      onRender: (item: UndefinedCoObjectCC) => (
        <span
          style={{ fontFamily: "monospace", color: "#0078d4", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => handleNavigateToContentControl(item.contentControlId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") void handleNavigateToContentControl(item.contentControlId);
          }}
          role="button"
          tabIndex={0}
        >
          {item.contentControlId}
        </span>
      ),
    },
    {
      key: "contentControlTitle",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.cctitle", "CC Title"),
      fieldName: "contentControlTitle",
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item: UndefinedCoObjectCC) => (
        <span
          style={{ cursor: "pointer", color: "#0078d4" }}
          onClick={() => handleNavigateToContentControl(item.contentControlId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") void handleNavigateToContentControl(item.contentControlId);
          }}
          role="button"
          tabIndex={0}
        >
          {item.contentControlTitle}
        </span>
      ),
    },
    {
      key: "fixLink",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.fixlink", "Fix Link"),
      minWidth: 250,
      maxWidth: 350,
      isResizable: true,
      onRender: (item: UndefinedCoObjectCC) => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Dropdown
            placeholder={formatMessageDefault(
              "cof.troubleshooting.docpartlinker.selectdocpart",
              "Select document part"
            )}
            options={getDropdownOptions()}
            selectedKey={selectedDocParts[item.contentControlId]}
            onChange={(_, option) => handleDocPartSelection(item.contentControlId, option?.key)}
            dropdownWidth="auto" // menu largo in base al contenuto
            calloutProps={{ calloutMinWidth: 150 }} // non scendere sotto la larghezza del controllo
            styles={{
              root: { width: 150 },
              dropdown: { width: 150 },
              title: { width: 150 }, // area valore selezionato sempre fissa
            }}
          />
          <PrimaryButton
            text={formatMessageDefault("cof.troubleshooting.docpartlinker.button.link", "Link")}
            onClick={() => handleLinkDocumentPart(item.contentControlId)}
            disabled={!selectedDocParts[item.contentControlId]}
            styles={{ root: { minWidth: "60px" } }}
          />
        </div>
      ),
    },
    {
      key: "contentControlTag",
      name: formatMessageDefault("cof.troubleshooting.docpartlinker.column.cctag", "CC Tag"),
      fieldName: "contentControlTag",
      minWidth: 200,
      isResizable: true,
      onRender: (item: UndefinedCoObjectCC) => (
        <span style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>{item.contentControlTag}</span>
      ),
    },
  ];

  return (
    <div style={{ padding: "1rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <p style={{ margin: "0.5rem 0", color: "var(--color-black)" }}>
          {formatMessageDefault(
            "cof.troubleshooting.docpartlinker.description",
            "This tool analyzes the relationship between Document Parts (Building Blocks) and Content Controls in the current document."
          )}
        </p>
      </div>

      <div style={{ marginBottom: "1.5rem" }}>
        <PrimaryButton
          text={formatMessageDefault("cof.troubleshooting.docpartlinker.button.analyze", "Analyze Links")}
          onClick={analyzeLinks}
          disabled={isLoading}
          iconProps={{ iconName: "Link" }}
        />
      </div>

      {isLoading && (
        <div style={{ padding: "2rem 0", textAlign: "center" }}>
          <Spinner
            size={SpinnerSize.large}
            label={formatMessageDefault(
              "cof.troubleshooting.docpartlinker.spinner.analyzing",
              "Analyzing document part links..."
            )}
          />
        </div>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          <strong>{formatMessageDefault("cof.troubleshooting.docpartlinker.error.prefix", "Error:")}</strong> {error}
        </MessageBar>
      )}

      {!isLoading && !error && result && (
        <div>
          {result.undefinedCoObjects.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <Separator alignContent="start">
                <div style={{ fontWeight: 600, fontSize: 16, color: "red" }}>
                  {formatMessageDefault(
                    "cof.troubleshooting.docpartlinker.undefined.title",
                    "⚠️ Body-Level Content Controls with Undefined CoObject"
                  )}{" "}
                  ({result.undefinedCoObjects.length})
                </div>
              </Separator>

              <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: "0.5rem" } }}>
                {formatMessageDefault(
                  "cof.troubleshooting.docpartlinker.undefined.warning",
                  "The following body-level content controls do not have a valid CoObject (empty or undefined). Select a document part from the dropdown to link it."
                )}
              </MessageBar>

              <div style={{ marginTop: "0.5rem" }}>
                <DetailsList
                  items={result.undefinedCoObjects}
                  columns={undefinedCoObjectColumns}
                  selectionMode={SelectionMode.none}
                  layoutMode={DetailsListLayoutMode.justified}
                  isHeaderVisible
                  styles={{
                    root: {
                      border: "1px solid orange",
                      borderRadius: 4,
                    },
                  }}
                />
              </div>
            </div>
          )}

          {result.linkedCount > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <Separator alignContent="start">
                <div style={{ fontWeight: 600, fontSize: 16, color: "green" }}>
                  {formatMessageDefault(
                    "cof.troubleshooting.docpartlinker.validlinks.title",
                    "✓ Valid Document Part Links"
                  )}{" "}
                  ({result.linkedCount})
                </div>
              </Separator>

              <div style={{ marginTop: "0.5rem" }}>
                <DetailsList
                  items={result.links.filter((link) => link.documentPartExists)}
                  columns={validLinksColumns}
                  selectionMode={SelectionMode.none}
                  layoutMode={DetailsListLayoutMode.justified}
                  isHeaderVisible
                  styles={{
                    root: {
                      border: "1px solid green",
                      borderRadius: 4,
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
